import { useIntl } from "react-intl";
import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useSupportBotContext } from "./SupportBotData";
import BotMaterials from "./BotMaterials";
import "./BotKnowledge.scss";
import {
    Loader2, AlertTriangle, X, ExternalLink, File, Eye, Plus
} from "lucide-react";
import {
    useVersionDifferences,
    VersionDifferenceIndicator
} from "./components/VersionDifferences";
import { CreateRagCorpus, createCorpusAndRegister } from "../../api/APIGemini";
import { GetCorpusCodes, PostCodes } from "../../api/APISurvey";
import { GetGeminiProject, GetGeminiLocation } from "../../api/APIBot";
import { useSnackbar } from "../../Content";
import { queryClient } from "../../App";

function deriveSlug(title, existingCorpora = {}) {
    const base = (title || 'kb').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || 'kb';
    let slug = base;
    let counter = 2;
    while (slug in existingCorpora) {
        slug = `${base}_${counter}`;
        counter++;
    }
    return slug;
}

// Gemini-specific Knowledge Base UI
function GeminiKnowledge({ botData, updateBotData }) {
    const intl = useIntl();
    const { setSnackbarData } = useSnackbar();
    const [showDropdown, setShowDropdown] = useState(false);
    const [filter, setFilter] = useState('');
    const [addingNew, setAddingNew] = useState(false);
    const [newKBTitle, setNewKBTitle] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [migrationDismissed, setMigrationDismissed] = useState(() => {
        const key = `kb_migration_dismissed_${botData.versionGroup || 'unknown'}`;
        return sessionStorage.getItem(key) === 'true';
    });

    const dropdownRef = useRef(null);
    const listRef = useRef(null);
    const [isScrollable, setIsScrollable] = useState(false);

    const corpora = botData.corpora || {};
    const legacyMaterials = (botData.materials || []).filter(m => m && (typeof m === 'string' || m.filename));

    const { getMissingCorpora, isCorpusAddedFromDefault } = useVersionDifferences();
    const missingCorpora = getMissingCorpora({ config: botData });

    const corpusCodesQuery = GetCorpusCodes();
    const corpusCodesMap = corpusCodesQuery.data?.[0] || {};

    const geminiProjectQuery = GetGeminiProject();
    const geminiLocationQuery = GetGeminiLocation();
    const createRagCorpusMutation = CreateRagCorpus();
    const postCodesMutation = PostCodes();

    // Build list of all available KBs not yet linked
    const allCorpusList = Object.entries(corpusCodesMap).map(([code, value]) => ({
        code,
        title: value?.title || code,
        resourceName: value?.resourceName
    }));

    const linkedCorpusIds = new Set(Object.values(corpora));
    const availableToAdd = allCorpusList.filter(
        kb => !linkedCorpusIds.has(kb.code)
    );
    const filteredAvailable = availableToAdd.filter(kb =>
        !filter || kb.title.toLowerCase().includes(filter.toLowerCase())
    );

    // Click outside handler
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
                setAddingNew(false);
                setNewKBTitle('');
                setFilter('');
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Scroll hint
    React.useEffect(() => {
        const element = listRef.current;
        if (element) {
            setIsScrollable(element.scrollHeight > element.clientHeight);
        }
    }, [filteredAvailable, showDropdown, filter]);

    const handleScroll = () => {
        const element = listRef.current;
        if (element) {
            setIsScrollable(element.scrollTop + element.clientHeight < element.scrollHeight);
        }
    };

    const dismissMigration = () => {
        const key = `kb_migration_dismissed_${botData.versionGroup || 'unknown'}`;
        sessionStorage.setItem(key, 'true');
        setMigrationDismissed(true);
    };

    const handleAddExisting = (kb) => {
        const slug = deriveSlug(kb.title, corpora);
        updateBotData('corpora', { ...corpora, [slug]: kb.code });
        setShowDropdown(false);
        setFilter('');
    };

    const handleRemoveCorpus = (slug) => {
        const updated = { ...corpora };
        delete updated[slug];
        updateBotData('corpora', updated);
    };

    const handleCreateNew = async () => {
        if (!newKBTitle.trim()) return;
        setIsCreating(true);
        try {
            const project = geminiProjectQuery?.data?.[0]?.value;
            const location = geminiLocationQuery?.data?.[0]?.value;
            const corpusBody = {
                displayName: newKBTitle.trim(),
            };
            if (project && location) {
                corpusBody.vectorDbConfig = {
                    ragEmbeddingModelConfig: {
                        vertexPredictionEndpoint: {
                            endpoint: `projects/${project}/locations/${location}/publishers/google/models/text-multilingual-embedding-002`
                        }
                    }
                };
            }

            const { corpusId } = await createCorpusAndRegister({
                title: newKBTitle.trim(),
                corpusBody,
                createMutation: createRagCorpusMutation,
                postCodesMutation,
            });

            queryClient.invalidateQueries('corpusCodes');

            const slug = deriveSlug(newKBTitle.trim(), corpora);
            updateBotData('corpora', { ...corpora, [slug]: corpusId });

            setSnackbarData({
                show: true,
                text: intl.formatMessage({ id: 'knowledge.createSuccess', defaultMessage: 'Knowledge base created successfully' }),
                state: "success", autoHide: true
            });

            setNewKBTitle('');
            setAddingNew(false);
            setShowDropdown(false);
        } catch (err) {
            console.error('Error creating Knowledge Base:', err);
            setSnackbarData({
                show: true,
                text: intl.formatMessage({ id: 'knowledge.createError', defaultMessage: 'Failed to create knowledge base. Please try again.' }),
                state: "error", autoHide: true
            });
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="bot-knowledge-gemini">
            {/* Migration notice */}
            {!migrationDismissed && legacyMaterials.length > 0 && Object.keys(corpora).length === 0 && (
                <div className="migration-notice">
                    <div className="migration-notice-header">
                        <AlertTriangle size={16} />
                        <strong>
                            {intl.formatMessage({ id: 'knowledge.migrationTitle', defaultMessage: 'Files need to be re-uploaded to a knowledge base' })}
                        </strong>
                        <button className="migration-dismiss" onClick={dismissMigration}><X size={14} /></button>
                    </div>
                    <p>
                        {intl.formatMessage({ id: 'knowledge.migrationBody', defaultMessage: 'This bot had files in the old materials system. Please create a knowledge base and re-upload these files:' })}
                    </p>
                    <ul>
                        {legacyMaterials.map((m, i) => (
                            <li key={i}>{typeof m === 'string' ? m : m.filename}</li>
                        ))}
                    </ul>
                </div>
            )}

            <h4 className="field-header">
                {intl.formatMessage({ id: 'knowledge', defaultMessage: 'Knowledge' })}
            </h4>

            {/* Linked Knowledge Bases */}
            <div className="linked-corpora-list">
                {Object.keys(corpora).length === 0 && (
                    <p className="knowledge-placeholder">
                        {intl.formatMessage({ id: 'knowledge.placeholder', defaultMessage: 'Link knowledge bases to give this bot access to relevant documents when forming responses.' })}
                    </p>
                )}

                {Object.entries(corpora).map(([slug, corpusId]) => {
                    const codeEntry = corpusCodesMap[corpusId];
                    const title = codeEntry?.title || slug;
                    const fileCount = codeEntry?.files ? Object.keys(codeEntry.files).length : null;
                    const isAdded = isCorpusAddedFromDefault({ config: botData }, corpusId);

                    return (
                        <div key={slug} className="linked-corpus-item">
                            <span className="linked-corpus-title">
                                {title}
                                {isAdded && <VersionDifferenceIndicator differenceTitle={intl.formatMessage({ id: 'knowledge.addedInVersion', defaultMessage: 'Added in this version' })} />}
                            </span>
                            {fileCount !== null && (
                                <span className="linked-corpus-meta">
                                    <File size={12} />
                                    {fileCount}
                                </span>
                            )}
                            <Link
                                to={`/bot-competences/knowledge/${corpusId}`}
                                className="linked-corpus-edit"
                                title={intl.formatMessage({ id: 'knowledge.editKB', defaultMessage: 'Edit Knowledge Base' })}
                            >
                                <Eye size={16} />
                            </Link>
                            <button
                                className="linked-corpus-unlink"
                                onClick={() => handleRemoveCorpus(slug)}
                                title={intl.formatMessage({ id: 'knowledge.unlink', defaultMessage: 'Unlink' })}
                            >
                                <X size={16} />
                            </button>
                        </div>
                    );
                })}

                {missingCorpora.map(({ slug, corpusId }) => {
                    const codeEntry = corpusCodesMap[corpusId];
                    const title = codeEntry?.title || slug;

                    return (
                        <div key={`missing-${slug}`} className="linked-corpus-item missing-corpus">
                            <span className="linked-corpus-title">
                                {title}
                                <VersionDifferenceIndicator differenceTitle={intl.formatMessage({ id: 'knowledge.missingInVersion', defaultMessage: 'Not included in this version' })} />
                            </span>
                            <button
                                className="linked-corpus-add"
                                onClick={() => handleAddExisting({ code: corpusId, title })}
                                title={intl.formatMessage({ id: 'bot.materials.add', defaultMessage: 'Add to this version' })}
                            >
                                <Plus size={18} />
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Knowledge Base dropdown (campaign-select pattern) */}
            <div className="campaign-dropdown" ref={dropdownRef}>
                <div
                    className="campaign-select"
                    onClick={() => setShowDropdown(!showDropdown)}
                    tabIndex="0"
                    onKeyUp={(e) => e.key === 'Enter' && setShowDropdown(!showDropdown)}
                >
                    {intl.formatMessage({ id: 'knowledge.chooseKB', defaultMessage: '+ Link a knowledge base' })}
                </div>
                {showDropdown && (
                    <div className="campaign-options">
                        <input
                            type="text"
                            placeholder={intl.formatMessage({ id: 'knowledge.filterKB', defaultMessage: 'Filter Knowledge Bases' })}
                            value={filter}
                            onChange={e => setFilter(e.target.value)}
                            autoFocus
                        />
                        <div className={`${isScrollable ? "show" : ""} scroll-hint-wrapper`}>
                            <div className="campaigns" ref={listRef} onScroll={handleScroll}>
                                {corpusCodesQuery.isLoading ? (
                                    <div><Loader2 size={14} className="spinner" /> {intl.formatMessage({ id: 'loading', defaultMessage: 'Loading...' })}</div>
                                ) : filteredAvailable.length > 0 ? (
                                    filteredAvailable.map(kb => (
                                        <div
                                            key={kb.code}
                                            className="campaign-option"
                                            onClick={() => handleAddExisting(kb)}
                                            tabIndex="0"
                                            onKeyUp={(e) => e.key === 'Enter' && handleAddExisting(kb)}
                                        >
                                            {kb.title}
                                        </div>
                                    ))
                                ) : (
                                    <em className="grey">{intl.formatMessage({ id: 'knowledge.noneAvailable', defaultMessage: 'No available knowledge bases' })}</em>
                                )}
                            </div>
                        </div>
                        {addingNew ? (
                            <div className="add-campaign">
                                <input
                                    type="text"
                                    placeholder={intl.formatMessage({ id: 'knowledge.titlePlaceholder', defaultMessage: 'Knowledge base title...' })}
                                    value={newKBTitle}
                                    onChange={e => setNewKBTitle(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && !isCreating) handleCreateNew(); }}
                                    autoFocus
                                />
                                <button onClick={handleCreateNew} disabled={!newKBTitle.trim() || isCreating}>
                                    {isCreating ? (
                                        <><Loader2 size={14} className="spinner" /> {intl.formatMessage({ id: 'creating', defaultMessage: 'Creating...' })}</>
                                    ) : (
                                        <>+ {intl.formatMessage({ id: 'knowledge.submitNew', defaultMessage: 'Create' })}</>
                                    )}
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setAddingNew(true)}
                                tabIndex="0"
                                onKeyUp={(e) => e.key === 'Enter' && setAddingNew(true)}
                            >
                                + {intl.formatMessage({ id: 'knowledge.createNew', defaultMessage: 'New Knowledge Base' })}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// Main component — delegates to Gemini or OpenAI path
const BotKnowledge = ({ botData, updateBotData }) => {
    const { aiProvider } = useSupportBotContext();
    const isGemini = aiProvider === "gemini";

    if (!isGemini) {
        return <BotMaterials botData={botData} updateBotData={updateBotData} />;
    }

    return <GeminiKnowledge botData={botData} updateBotData={updateBotData} />;
};

export default BotKnowledge;
